import {
  Banner,
  Button,
  Card,
  HSpacer,
  IconButton,
  InlineMessage,
  Input,
  Modal,
  NumericInput,
  Text,
  VSpacer,
} from '@/components/DesignSystem';
import { ApiEnergyProgramDetail } from '@api/interfaces/ApiEnergyProgramDetail';
import Add from '@mui/icons-material/Add';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  useTheme,
} from '@mui/material';
import { SharedConfig } from '@shared/SharedConfig';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EnergyBehaviorCard } from './EnergyBehaviorCard';
import {
  effectiveRate,
  EnergyTierForm,
  raiseBehaviorsRequiredToRequiredCount,
  totalBehaviorsRequired,
  validateEnergyTiers,
} from './energyValidation';

interface EnergyTiersModalProps {
  apiError?: string | null,
  isSaving?: boolean,
  onClose: () => void,
  onSave: (tiers: EnergyTierForm[]) => void,
  program: ApiEnergyProgramDetail,
}

const { rewardsMaxTiers } = SharedConfig.settings;

const toForm = (program: ApiEnergyProgramDetail): EnergyTierForm[] => program.tiers.map((tier) => ({
  behaviors: tier.behaviors.map((behavior) => ({
    completionCount: behavior.completionCount,
    description: behavior.description,
    id: behavior.id,
    isRequiredForTier: behavior.isRequiredForTier,
    name: behavior.name,
    sku: behavior.sku ?? '',
  })),
  behaviorsRequired: tier.behaviorsRequired,
  name: tier.name,
  pointsMultiplier: tier.pointsMultiplier,
}));

const emptyBehavior = () => ({
  completionCount: 0,
  description: '',
  isRequiredForTier: false,
  name: '',
  sku: '',
});

export const EnergyTiersModal = ({
  apiError,
  isSaving = false,
  onClose,
  onSave,
  program,
}: EnergyTiersModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [tiers, setTiers] = useState<EnergyTierForm[]>(() => toForm(program));
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showErrors, setShowErrors] = useState(false);
  const error = validateEnergyTiers(tiers);
  const isStructureLocked = program.memberCount > 0;
  const areRulesLocked = program.hasEnergyPoints;
  const selectedTier = tiers[selectedIndex];
  const isBaseTier = selectedIndex === 0;

  const updateTier = (index: number, patch: Partial<EnergyTierForm>) => {
    const updated = tiers.map((tier, i) => (i === index ? { ...tier, ...patch } : tier));
    setTiers(patch.behaviors ? raiseBehaviorsRequiredToRequiredCount(updated) : updated);
  };

  const addTier = () => {
    const previous = tiers[tiers.length - 1];
    setTiers([...tiers, {
      behaviors: [emptyBehavior()],
      behaviorsRequired: 1,
      name: '',
      pointsMultiplier: Math.floor(previous.pointsMultiplier) + 1,
    }]);
    setSelectedIndex(tiers.length);
  };

  const removeTier = (index: number) => {
    setTiers(tiers.filter((_, i) => i !== index));
    setSelectedIndex(Math.max(0, Math.min(selectedIndex, tiers.length - 2)));
  };

  const handleSave = () => {
    if (error) {
      setShowErrors(true);
      setSelectedIndex(error.tierIndex);
      return;
    }
    onSave(tiers);
  };

  const visibleError = showErrors && error;
  const errorText = visibleError ? t(($) => $.energy.tiers.errors[error.key]) : undefined;
  const { cost, points } = effectiveRate(
    program.basePointsPerGallon,
    selectedTier?.pointsMultiplier ?? 1,
  );

  const TierList = (
    <Stack minWidth="300px">
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Text category="body-large">
          {t(($) => $.energy.tiers.tierCount, { count: tiers.length, max: rewardsMaxTiers })}
        </Text>
        {!isStructureLocked && !areRulesLocked && tiers.length < rewardsMaxTiers && (
          <Button
            onClick={addTier}
            startIcon={<Add />}
            testID="energy-tiers-add"
            variant="text"
          >
            {t(($) => $.energy.tiers.addTier)}
          </Button>
        )}
      </Stack>
      <List sx={{ paddingX: '1px' }}>
        {tiers.map((tier, index) => (
          <ListItem
            disablePadding
            key={index}
            secondaryAction={(index === 0 || isStructureLocked || areRulesLocked) ? (
              <Text category="label-medium">
                {index === 0 ? t(($) => $.energy.tiers.baseTier) : ''}
              </Text>
            ) : (
              <IconButton
                aria-label={t(($) => $.energy.tiers.removeTier, {
                  name: tier.name || t(($) => $.energy.tiers.untitled),
                })}
                onClick={(event) => {
                  event.stopPropagation();
                  removeTier(index);
                }}
                testID={`energy-tiers-remove-${index}`}
              >
                <DeleteOutline />
              </IconButton>
            )}
            sx={[
              {
                bgcolor: theme.palette.background.paper,
                borderRadius: '8px',
                height: '56px',
                marginBottom: '8px',
              },
              index === selectedIndex
                ? { outline: `1px solid ${theme.palette.primary.main}` }
                : { cursor: 'pointer' },
              visibleError && error.tierIndex === index
                ? { outline: `1px solid ${theme.palette.error.main}` }
                : {},
            ]}
          >
            <ListItemButton
              onClick={() => setSelectedIndex(index)}
              selected={index === selectedIndex}
              sx={{ borderRadius: '8px', height: '100%' }}
            >
              <ListItemText
                data-testid={`energy-tiers-item-${index}`}
                primary={tier.name || t(($) => $.energy.tiers.untitled)}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Stack>
  );

  const TierEditor = selectedTier && (
    <Card sx={{ maxHeight: 620, overflowY: 'auto', width: '100%' }} testID="energy-tiers-editor">
      <Text category="headline-small">
        {t(($) => $.energy.tiers.details)}
      </Text>
      <VSpacer size="5" />
      <Input
        label={t(($) => $.energy.tiers.tierName)}
        onChangeText={(name) => updateTier(selectedIndex, { name })}
        testID="energy-tier-name"
        value={selectedTier.name}
      />
      <VSpacer size="5" />
      <NumericInput
        decimals={1}
        disabled={isBaseTier || areRulesLocked}
        helperText={t(($) => $.energy.tiers.effectiveRate, { cost, points })}
        label={t(($) => $.energy.tiers.multiplier)}
        onChangeNumber={(value) => updateTier(selectedIndex, { pointsMultiplier: value ?? 0 })}
        suffix="×"
        testID="energy-tier-multiplier"
        value={selectedTier.pointsMultiplier}
      />
      <VSpacer size="5" />
      <NumericInput
        decimals={0}
        disabled={areRulesLocked}
        helperText={isBaseTier
          ? t(($) => $.energy.tiers.behaviorsRequiredBaseHint)
          : t(($) => $.energy.tiers.behaviorsRequiredHint)}
        label={t(($) => $.energy.tiers.behaviorsRequired)}
        onChangeNumber={(value) => updateTier(selectedIndex, { behaviorsRequired: value ?? 0 })}
        testID="energy-tier-behaviors-required"
        value={selectedTier.behaviorsRequired}
      />
      {!isBaseTier && (
        <>
          <VSpacer size="3" />
          <Stack data-testid="energy-tier-total-behaviors-required">
            <Banner
              icon={<InfoOutlined />}
              messages={[t(($) => $.energy.tiers.totalBehaviorsRequiredBreakdown, {
                breakdown: tiers.slice(1, selectedIndex + 1).map((tier) => (
                  t(($) => $.energy.tiers.totalBehaviorsRequiredItem, {
                    count: tier.behaviorsRequired,
                    name: tier.name || t(($) => $.energy.tiers.untitled),
                  })
                )).join(' + '),
              })]}
              status="info"
              title={t(($) => $.energy.tiers.totalBehaviorsRequired, {
                count: totalBehaviorsRequired(tiers, selectedIndex),
              })}
              titleTextCategory="title-medium"
              variant="standard"
            />
          </Stack>
        </>
      )}
      <VSpacer size="7" />
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Text category="title-medium">
          {t(($) => $.energy.tiers.behaviorsTitle)}
        </Text>
        {!areRulesLocked && (
          <Button
            onClick={() => updateTier(selectedIndex, {
              behaviors: [...selectedTier.behaviors, emptyBehavior()],
            })}
            startIcon={<Add />}
            testID="energy-tier-add-behavior"
            variant="text"
          >
            {t(($) => $.energy.tiers.addBehavior)}
          </Button>
        )}
      </Stack>
      <Text category="body-medium" sx={{ color: 'text.secondary' }}>
        {isBaseTier
          ? t(($) => $.energy.tiers.behaviorsBaseHelper)
          : t(($) => $.energy.tiers.behaviorsHelper)}
      </Text>
      <VSpacer size="4" />
      {!selectedTier.behaviors.length && !isBaseTier && (
        <Text category="body-medium" sx={{ color: 'error.main' }} testID="energy-tier-no-behaviors">
          {t(($) => $.energy.tiers.errors.noBehaviorsDefined)}
        </Text>
      )}
      {selectedTier.behaviors.map((behavior, behaviorIndex) => (
        <Stack key={behavior.id ?? `new-${behaviorIndex}`}>
          <EnergyBehaviorCard
            behavior={behavior}
            errorText={visibleError && error.tierIndex === selectedIndex
              && error.behaviorIndex === behaviorIndex ? errorText : undefined}
            index={behaviorIndex}
            isRuleLocked={areRulesLocked}
            onChange={(updated) => updateTier(selectedIndex, {
              behaviors: selectedTier.behaviors.map((current, i) => (
                i === behaviorIndex ? updated : current
              )),
            })}
            onRemove={() => updateTier(selectedIndex, {
              behaviors: selectedTier.behaviors.filter((_, i) => i !== behaviorIndex),
            })}
            testID={`energy-behavior-${selectedIndex}-${behaviorIndex}`}
          />
          <VSpacer size="4" />
        </Stack>
      ))}
    </Card>
  );

  return (
    <Modal
      acceptButton={(props) => (
        <Button
          {...props}
          disabled={isSaving}
          loading={isSaving}
          onClick={handleSave}
          testID="energy-tiers-save"
        >
          {t(($) => $.common.actions.save)}
        </Button>
      )}
      cancelButton={(props) => (
        <Button {...props} onClick={onClose} testID="energy-tiers-cancel">
          {t(($) => $.common.actions.cancel)}
        </Button>
      )}
      largeModal
      onClose={onClose}
      open
      testID="energy-tiers-modal"
      title={t(($) => $.energy.tiers.title)}
    >
      <Stack
        data-testid="energy-tiers-messages"
        sx={{
          bgcolor: theme.palette.background.default,
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <Text category="body-large">{t(($) => $.energy.tiers.intro)}</Text>
        {(areRulesLocked || isStructureLocked) && (
          <>
            <VSpacer size="5" />
            <InlineMessage
              icon={<InfoOutlined color="info" />}
              testID="energy-tiers-locked"
              title={areRulesLocked
                ? t(($) => $.energy.tiers.rulesLocked)
                : t(($) => $.energy.tiers.tierStructureLocked)}
            />
          </>
        )}
        {(!!apiError || (visibleError && error.behaviorIndex === undefined)) && (
          <>
            <VSpacer size="5" />
            <Text category="body-medium" sx={{ color: 'error.main' }} testID="energy-tiers-error">
              {apiError ?? errorText}
            </Text>
          </>
        )}
        <VSpacer size="6" />
      </Stack>
      <Stack direction="row">
        {TierList}
        <HSpacer size="7" />
        {TierEditor}
      </Stack>
    </Modal>
  );
};
